/**
 * One fact: its value, where it came from, how sure the source was, and — if a
 * human may correct it — an inline editor.
 *
 * Decisions worth keeping:
 *
 * · **A missing value is not a blank cell.** `missing: true` renders the words
 *   "Eingabewert fehlt" with its own glyph and a primary "Wert eintragen"
 *   action. Never "0", never "—": a dash in a table of numbers reads like data,
 *   and the brief's "null over guess" is worthless if the UI quietly turns null
 *   back into something that looks measured.
 * · **The edit affordance is always visible.** No hover-only pencil: it is a
 *   real, focusable, labelled button in the tab order, with the field name in
 *   its accessible name so a screen-reader user knows which "Ändern" they are on.
 * · **The editor's shape follows the value's type, and the server has the last
 *   word.** A boolean gets ja/nein/leer, a number gets a numeric input, a list
 *   gets a comma-separated line. The client shapes the input; it never decides
 *   whether the value is *allowed* — the API answers that, and the API's own
 *   sentence is what the reader sees on a rejection.
 * · **`parcel` and `case_id` are not editable** — the backend keeps them off the
 *   allowlist so the audit trail (which records old → new) stays free of
 *   personal data. The row shows that as a stated reason, not as a dead button.
 */
import { useEffect, useId, useRef, useState } from 'react'
import { CircleDashed, Lock, PenLine } from 'lucide-react'

import { Icon } from '@/components/Icon'
import { ConfidenceMeter, ProvenanceBadge } from '@/components/ProvenanceBadge'
import { Button } from '@/components/ui/button'
import { editorKind, parseInput, toInputValue } from '@/features/facts/factEditors'
import { useI18n } from '@/i18n'
import { cn, formatNumber } from '@/lib/utils'
import type { FactField, FactValue } from '@/api/types'

export function FactRow({
  field,
  busy,
  onSave,
}: {
  field: FactField
  busy: boolean
  onSave: (path: string, value: FactValue) => Promise<void> | void
}) {
  const { t } = useI18n()
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState('')
  const inputId = useId()
  const inputRef = useRef<HTMLInputElement | HTMLSelectElement | null>(null)
  const kind = editorKind(field)

  useEffect(() => {
    if (editing) inputRef.current?.focus()
  }, [editing])

  const start = () => {
    setDraft(toInputValue(kind, field.value ?? null))
    setEditing(true)
  }

  const submit = async () => {
    await onSave(field.path, parseInput(kind, draft))
    setEditing(false)
  }

  return (
    <li
      data-testid="fact-row"
      data-path={field.path}
      data-provenance={field.provenance}
      data-missing={field.missing ? 'true' : 'false'}
      data-editable={field.editable ? 'true' : 'false'}
      className="flex flex-col gap-3 border-b border-border px-4 py-3 last:border-b-0"
    >
      {/* Fixed columns, not `auto`: an auto-sized last column changes width per
          row, which pushes the value column around and destroys the vertical
          alignment of a table of numbers — the one thing a planner scans. */}
      <div className="flex flex-col gap-3 md:grid md:grid-cols-[minmax(0,1.6fr)_minmax(0,1fr)_15rem_8rem] md:items-center md:gap-4">
        <div className="flex min-w-0 flex-col">
          <span className="text-[14px] text-ink">{field.label_de}</span>
          <span className="num text-[11px] text-muted-2">{field.path}</span>
        </div>

        <div className="flex min-w-0 flex-col gap-1">
          <FactValueCell field={field} />
          {field.source_note ? (
            <span className="text-[11px] text-muted">{field.source_note}</span>
          ) : null}
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <ProvenanceBadge provenance={field.provenance} />
          <ConfidenceMeter confidence={field.confidence} />
        </div>

        <div className="flex items-center md:justify-end">
          {field.editable ? (
            <Button
              size="sm"
              variant={field.missing ? 'primary' : 'secondary'}
              onClick={start}
              disabled={busy || editing}
              aria-label={`${field.missing ? t('facts.enter') : t('facts.edit')} — ${field.label_de}`}
            >
              <Icon icon={PenLine} />
              {field.missing ? t('facts.enter') : t('facts.edit')}
            </Button>
          ) : null}
        </div>
      </div>

      {/* Why there is no editor here — a full-width line rather than a squeezed
          column, because the reason is a sentence and deserves to be read. */}
      {field.editable ? null : (
        <p data-testid="fact-locked" className="flex items-start gap-2 text-[11px] text-muted-2">
          <Icon icon={Lock} className="mt-0.5 size-3" />
          {t(field.group === 'gis' ? 'facts.notEditableGis' : 'facts.notEditable')}
        </p>
      )}

      {editing ? (
        <form
          data-testid="fact-editor"
          className="flex flex-wrap items-end gap-3 rounded-sm border border-border-strong bg-surface px-4 py-3"
          onSubmit={(event) => {
            event.preventDefault()
            void submit()
          }}
        >
          <div className="flex min-w-[16rem] flex-col gap-1">
            <label htmlFor={inputId} className="text-[12px] font-medium text-ink-2">
              {field.label_de}
              {field.unit ? ` (${field.unit})` : ''}
            </label>
            {kind === 'boolean' ? (
              <select
                id={inputId}
                ref={(node) => {
                  inputRef.current = node
                }}
                value={draft}
                onChange={(event) => setDraft(event.target.value)}
                className="h-10 rounded-sm border border-border-strong bg-white px-3 text-[15px] text-ink"
              >
                <option value="true">{t('common.yes')}</option>
                <option value="false">{t('common.no')}</option>
                <option value="">{t('facts.clear')}</option>
              </select>
            ) : (
              <input
                id={inputId}
                ref={(node) => {
                  inputRef.current = node
                }}
                value={draft}
                inputMode={kind === 'number' ? 'decimal' : undefined}
                onChange={(event) => setDraft(event.target.value)}
                placeholder={kind === 'list' ? t('facts.listPlaceholder') : undefined}
                className="h-10 rounded-sm border border-border-strong bg-white px-3 text-[15px] text-ink"
              />
            )}
            <span className="text-[11px] text-muted">
              {kind === 'list' ? t('facts.listHelp') : t('facts.editHelp')}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <Button size="sm" type="submit" disabled={busy}>
              {t('facts.save')}
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setEditing(false)} disabled={busy}>
              {t('common.cancel')}
            </Button>
          </div>
        </form>
      ) : null}
    </li>
  )
}

/** The value itself — or, when there is none, the fact that there is none. */
export function FactValueCell({ field }: { field: FactField }) {
  const { t } = useI18n()

  if (field.missing || field.value === null || field.value === undefined) {
    return (
      <span
        data-testid="fact-missing"
        className="inline-flex items-center gap-2 text-[13px] font-medium text-signal-amber-text"
      >
        <Icon icon={CircleDashed} className="size-3.5" />
        {t('facts.valueMissing')}
      </span>
    )
  }

  if (Array.isArray(field.value)) {
    return (
      <span className="flex flex-wrap gap-1.5">
        {field.value.map((item) => (
          <span
            key={item}
            className="rounded-pill border border-border bg-surface px-2 py-0.5 text-[12px] text-ink-2"
          >
            {item}
          </span>
        ))}
      </span>
    )
  }

  return (
    <span className={cn('num text-[15px] text-ink')}>
      {formatNumber(field.value, { yes: t('common.yes'), no: t('common.no') })}
      {field.unit ? <span className="ms-1 text-[12px] text-muted">{field.unit}</span> : null}
    </span>
  )
}
